import { useState, useEffect } from 'react';
import { getCheckoutUrl } from '../components/stripePayments';
import { getPremiumStatus, getSubscriptionDetails } from '../components/getPremiumStatus';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Modal from 'react-modal';

const STRIPE_PRICE_IDS = {
  weekly: 'price_1SHY0cJcMfFv41GS0vap9SyS', // live
  monthly: 'price_1RxCpCJcMfFv41GSDg7XdVcw', // live
  yearly: 'price_1RzGp0JcMfFv41GSBrskKB6S', // live
};

const GOOGLE_PRICE_IDS = {
  weekly: 'premium_weekly',
  monthly: 'premium_monthly',
  yearly: 'premium_yearly',
};

export const Pricing = () => {
  const [loading, setLoading] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [showNotice, setShowNotice] = useState(false);
  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;
  const { enqueueSnackbar } = useSnackbar();
  const authUID = auth.currentUser?.uid;

  useEffect(() => {
    if (user) {
      getPremiumStatus(auth.app).then(setIsPremium);
      getSubscriptionDetails(auth.app).then((sub) => {
        if (sub) {
          setActiveSubscription(sub);
        }
      });
    }
  }, [user, auth.app]);

  const getActiveInterval = () => {
    if (!activeSubscription) return null;
    if (activeSubscription.plan && activeSubscription.plan !== 'unknown') {
      return activeSubscription.plan;
    }
    const isGoogleSub = activeSubscription.type === 'googlePay';
    const isStripeSub = activeSubscription.type === 'stripe';
    const sub = activeSubscription;
    if (isGoogleSub) {
      if (sub.subscriptionId) {
        if (GOOGLE_PRICE_IDS.weekly === sub.subscriptionId) return 'weekly';
        if (GOOGLE_PRICE_IDS.monthly === sub.subscriptionId) return 'monthly';
        if (GOOGLE_PRICE_IDS.yearly === sub.subscriptionId) return 'yearly';
      }
    } else if (isStripeSub) {
      const priceID = sub.priceId;
      if (priceID === STRIPE_PRICE_IDS.weekly) return 'weekly';
      if (priceID === STRIPE_PRICE_IDS.monthly) return 'monthly';
      if (priceID === STRIPE_PRICE_IDS.yearly) return 'yearly';
    }
    return null;
  };

  const activeInterval = isPremium ? getActiveInterval() : null;

  const startCheckout = async (priceId, planKey) => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (activeSubscription || isPremium) {
      setShowNotice(true);
      enqueueSnackbar("You already have an active subscription.", {
        variant: 'warning',
        autoHideDuration: 6000,
      });
      return;
    }

    try {
      setLoading(true);
      const url = await getCheckoutUrl(priceId, planKey);
      window.location.assign(url);
    } catch (e) {
      console.error("Checkout error:", e);
      if (e.details) {
        setActiveSubscription({
          provider: e.details.provider,
          plan: e.details.plan,
          status: e.details.status,
          expiresAt: e.details.expiresAt,
          autoRenew: e.details.autoRenew,
          message: e.details.message,
        });
        setShowNotice(true);
      } else {
        alert(e.message || 'Checkout failed.');
      }
      setLoading(false);
    }
  };

  const getProviderDisplayName = (sub) => {
    const prov = String(sub?.canonicalProvider || sub?.provider || sub?.type || '').toLowerCase();
    if (prov.includes('apple')) return 'Apple App Store';
    if (prov.includes('google') || prov.includes('android')) return 'Google Play';
    if (prov.includes('stripe')) return 'Flintt Website (Stripe)';
    if (prov.includes('lifetime')) return 'Lifetime Premium';
    return 'Active Subscription';
  };

  const formattedExpiry = activeSubscription?.expiresAt
    ? new Date(activeSubscription.expiresAt).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : null;

  return (
    <div className="main pricing">
      <div className='aside'><div></div></div>

      <div className='content has-aside static'>
        <Modal
          className="upgrade-prompt"
          isOpen={showNotice}
          onRequestClose={() => setShowNotice(false)}
          contentLabel="Subscription Active"
        >
          <h2>Subscription Already Active</h2>
          <div style={{ margin: '14px 0', lineHeight: 1.6, fontSize: '15px' }}>
            <p style={{ marginBottom: '10px' }}>
              You already have an active Premium entitlement on this account.
            </p>
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid rgba(238, 220, 197, 0.2)',
              padding: '12px 16px',
              borderRadius: '10px',
              marginBottom: '12px',
              textAlign: 'left'
            }}>
              <div style={{ marginBottom: '4px' }}>
                <strong>Provider: </strong>
                {getProviderDisplayName(activeSubscription)}
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>Plan: </strong>
                {activeSubscription?.plan ? String(activeSubscription.plan).toUpperCase() : 'PREMIUM'}
              </div>
              {formattedExpiry && (
                <div>
                  <strong>Active until: </strong>
                  {formattedExpiry}
                </div>
              )}
            </div>
            <p style={{ fontSize: '13px', opacity: 0.9 }}>
              You can start a new subscription once your current billing period ends. You can manage your existing subscription in account settings.
            </p>
          </div>
          <div className="actions" style={{ marginTop: '16px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button onClick={() => setShowNotice(false)}>Close</button>
            <button
              onClick={() => {
                setShowNotice(false);
                navigate(`/subscription/${authUID || user?.uid || ''}`);
              }}
            >
              Manage Subscription
            </button>
          </div>
        </Modal>

        <div className='intro'>
          <h1>Pricing</h1>
        </div>

        <h2>Premium vs Free plan features</h2>

        <div className="features">
          <div>.</div>
          <div className='title premium'><h3>Premium</h3></div>
          <div className='title free'><h3>Free</h3></div>
          <div className='feature'><p>Sending Messages</p></div>
          <div><p>Unlimited</p></div>
          <div><p>20 per day</p></div>
          <div className='feature'><p>Viewing Profiles</p></div>
          <div><p>Unlimited</p></div>
          <div><p>20 per day</p></div>
          <div className='feature'><p>Photo Uploads</p></div>
          <div><p>25</p></div>
          <div><p>5</p></div>
          <div className='feature'><p>Video Uploads</p></div>
          <div><p>5</p></div>
          <div><p>0</p></div>
          <div className='feature'><p>Browse Invisibly</p></div>
          <div><p>✓</p></div>
          <div><p>X</p></div>
          <div className='feature'><p>Read Receipts</p></div>
          <div><p>✓</p></div>
          <div><p>✓</p></div>
          <div className='feature'><p>Search Filters</p></div>
          <div><p>Sort by: age, distance, meeting location, interests, online</p></div>
          <div><p>Sort by: age, distance</p></div>
          <div className='feature'><p>Delete Messages</p></div>
          <div><p>✓</p></div>
          <div><p>X</p></div>
          <div className='feature'><p>Image Message Storage</p></div>
          <div><p>6 months</p></div>
          <div><p>1 month</p></div>
          <div className='feature'><p>Custom Email Notifications</p></div>
          <div><p>✓</p></div>
          <div><p>X</p></div>
        </div>

        <div className="pricing-options">
          <div className="pricing-option">
            <h2>Weekly</h2>
            <span>£2/week</span>
            <p>The cheapest Flintt plan. Lasts for 7 days.</p>
            {activeInterval === 'weekly' ? (
              <button className="pricing-button" disabled>Active</button>
            ) : (
              <button className="pricing-button" disabled={loading} onClick={() => startCheckout(STRIPE_PRICE_IDS.weekly, 'weekly')}>
                {loading ? 'Redirecting…' : 'Start Weekly'}
              </button>
            )}
          </div>

          <div className="pricing-option">
            <h2>Monthly</h2>
            <span>£6/month</span>
            <p>Lasts for one calendar month of purchase.</p>
            {activeInterval === 'monthly' ? (
              <button className="pricing-button" disabled>Active</button>
            ) : (
              <button className="pricing-button" disabled={loading} onClick={() => startCheckout(STRIPE_PRICE_IDS.monthly, 'monthly')}>
                {loading ? 'Redirecting…' : 'Start Monthly'}
              </button>
            )}
          </div>

          <div className="pricing-option">
            <h2>Yearly</h2>
            <span>£60/year</span>
            <p>The best value plan. Lasts for 365 days.</p>
            {activeInterval === 'yearly' ? (
              <button className="pricing-button" disabled>Active</button>
            ) : (
              <button className="pricing-button" disabled={loading} onClick={() => startCheckout(STRIPE_PRICE_IDS.yearly, 'yearly')}>
                {loading ? 'Redirecting…' : 'Start Yearly'}
              </button>
            )}
            <div>
              <p>Please carefully read the <a href="/terms-of-service">Terms of Service</a> before purchasing your subscription.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
